// ==============================================================================
// DASHBOARD ESPECÍFICO - ATLETA
// ==============================================================================

// src/components/dashboard/AthleteDashboard.js
import React from 'react';
import { BaseDashboard } from './BaseDashboard';

// Imports de Material-UI
import {
  Avatar,
  Box,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  IconButton
} from '@mui/material';

// Imports de iconos
import {
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  Speed as SpeedIcon,
  FitnessCenter as FitnessIcon,
  Timeline as TimelineIcon,
  Star as StarIcon
} from '@mui/icons-material';

// Widget de bienvenida del atleta
const AthleteWelcomeWidget = ({ user }) => (
  <Box>
    <Box display="flex" alignItems="center" mb={3}>
      <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 56, height: 56 }}>
        🏆
      </Avatar>
      <Box>
        <Typography variant="h5" gutterBottom>
          ¡Hola, {user?.displayName || 'Atleta'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Tu camino hacia la excelencia
        </Typography>
      </Box>
    </Box>
    
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Button 
          variant="contained" 
          fullWidth 
          size="small" 
          color="info"
          startIcon={<TrendingUpIcon />}
        >
          Mi Progreso
        </Button>
      </Grid>
      <Grid item xs={6}>
        <Button 
          variant="outlined" 
          fullWidth 
          size="small" 
          color="info"
          startIcon={<ScheduleIcon />}
        >
          Entrenamientos
        </Button>
      </Grid>
    </Grid>
  </Box>
);

// Widget de estadísticas del atleta
const AthleteStatsWidget = () => (
  <Box>
    <Box textAlign="center" mb={2}>
      <Typography variant="h4" color="info.main" fontWeight="bold">
        10.45s
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Mejor tiempo 100m
      </Typography>
      <Chip 
        label="Récord personal" 
        color="success" 
        size="small"
        icon={<StarIcon />}
      />
    </Box>
    
    <Divider sx={{ my: 2 }} />
    
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Progreso hacia meta: Sub 10.40s
      </Typography>
      <LinearProgress 
        variant="determinate" 
        value={85} 
        sx={{ height: 8, borderRadius: 4, mb: 1 }}
        color="info"
      />
      <Box display="flex" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          Meta: 10.40s
        </Typography>
        <Typography variant="caption" color="success.main">
          85%
        </Typography>
      </Box>
    </Box>
  </Box>
);

// Widget de próximo entrenamiento
const NextTrainingWidget = () => (
  <Box>
    <Box textAlign="center" mb={2}>
      <Typography variant="h4" color="primary.main" fontWeight="bold">
        16:30
      </Typography>
      <Typography variant="h6" gutterBottom>
        Trabajo de velocidad
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        con Coach Martínez
      </Typography>
      <Typography variant="caption" color="text.secondary">
        📍 Pista Principal • ⏱️ 90 minutos
      </Typography>
    </Box>
    
    <Button 
      variant="outlined" 
      fullWidth 
      size="small"
      startIcon={<FitnessIcon />}
    >
      Ver Detalles del Entrenamiento
    </Button>
  </Box>
);

// Widget de competencias del atleta
const AthleteCompetitionsWidget = () => {
  const competitions = [
    { 
      name: 'Regional 100m', 
      date: '15 Mar', 
      status: 'Confirmado',
      location: 'Estadio Municipal'
    },
    { 
      name: 'Nacional 200m', 
      date: '22 Mar', 
      status: 'Inscrito',
      location: 'Complejo Deportivo'
    },
    { 
      name: 'Copa Juvenil', 
      date: '5 Abr', 
      status: 'Pendiente',
      location: 'Pista Universitaria'
    }
  ];

  return (
    <List dense>
      {competitions.map((comp, index) => (
        <ListItem key={index} divider={index < competitions.length - 1}>
          <ListItemText
            primary={
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" fontWeight="medium">
                  {comp.name}
                </Typography>
                <Chip 
                  label={comp.status}
                  size="small"
                  color={
                    comp.status === 'Confirmado' ? 'success' :
                    comp.status === 'Inscrito' ? 'info' : 'warning'
                  }
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="caption" color="text.secondary">
                  📅 {comp.date} • 📍 {comp.location}
                </Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

// Widget de progreso de metas
const AthleteProgressWidget = () => {
  const goals = [
    { name: 'Sub 10.40 en 100m', progress: 85, target: '15 Mar', color: 'info' },
    { name: '5 competencias', progress: 60, target: 'Este año', color: 'primary' },
    { name: 'Técnica de salida', progress: 90, target: 'Continuo', color: 'success' }
  ];

  return (
    <Box>
      {goals.map((goal, index) => (
        <Box key={index} mb={index < goals.length - 1 ? 3 : 0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" fontWeight="medium">
              {goal.name}
            </Typography>
            <Typography variant="caption" color={`${goal.color}.main`}>
              {goal.progress}%
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={goal.progress} 
            sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
            color={goal.color}
          />
          
          <Typography variant="caption" color="text.secondary">
            Meta: {goal.target}
          </Typography>
          
          {index < goals.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
    </Box>
  );
};

// Widget de resultados recientes
const RecentResultsWidget = () => {
  const results = [
    { event: 'Regional 100m', time: '10.45s', position: '1°', date: '5 Mar', medal: '🥇' },
    { event: 'Nacional 200m', time: '21.23s', position: '3°', date: '20 Feb', medal: '🥉' },
    { event: 'Copa Estatal', time: '10.67s', position: '2°', date: '15 Feb', medal: '🥈' }
  ];

  return (
    <List dense>
      {results.map((result, index) => (
        <ListItem key={index} divider={index < results.length - 1}>
          <ListItemText
            primary={
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight="medium">
                  {result.event}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2">{result.medal}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {result.position}
                  </Typography>
                </Box>
              </Box>
            }
            secondary={
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="primary.main">
                  ⏱️ {result.time}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {result.date}
                </Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

// Componente principal del dashboard del atleta
export const AthleteDashboard = ({ user, onWidgetsChange }) => {
  const athleteWidgets = [
    { 
      id: 'athlete-welcome', 
      title: null, // Sin título para que se vea más limpio
      size: { xs: 12, md: 8 }, 
      component: AthleteWelcomeWidget,
      order: 1
    },
    { 
      id: 'athlete-stats', 
      title: 'Mis Estadísticas', 
      size: { xs: 12, md: 4 }, 
      component: AthleteStatsWidget,
      order: 2
    },
    { 
      id: 'athlete-next-training', 
      title: 'Próximo Entrenamiento', 
      size: { xs: 12, lg: 6 }, 
      component: NextTrainingWidget,
      order: 3
    },
    { 
      id: 'athlete-competitions', 
      title: 'Mis Competencias', 
      size: { xs: 12, lg: 6 }, 
      component: AthleteCompetitionsWidget,
      order: 4
    },
    { 
      id: 'athlete-progress', 
      title: 'Progreso de Metas', 
      size: { xs: 12, lg: 8 }, 
      component: AthleteProgressWidget,
      order: 5
    },
    { 
      id: 'athlete-recent-results', 
      title: 'Resultados Recientes', 
      size: { xs: 12, lg: 4 }, 
      component: RecentResultsWidget,
      order: 6
    }
  ];

  // Acciones personalizadas para el SpeedDial
  const customActions = [
    {
      icon: <TrendingUpIcon />,
      tooltip: "Ver Mi Progreso",
      onClick: () => console.log('Abrir progreso detallado')
    },
    {
      icon: <ScheduleIcon />,
      tooltip: "Próximos Entrenamientos",
      onClick: () => console.log('Ver calendario de entrenamientos')
    },
    {
      icon: <EventIcon />,
      tooltip: "Mis Competencias",
      onClick: () => console.log('Ver todas las competencias')
    },
    {
      icon: <TimelineIcon />,
      tooltip: "Historial de Resultados",
      onClick: () => console.log('Ver historial completo')
    }
  ];

  return (
    <BaseDashboard
      role="atleta"
      user={user}
      widgets={athleteWidgets}
      title="Panel Personal"
      subtitle="Sigue tu progreso y alcanza tus metas deportivas"
      roleIcon="🏆"
      roleColor="info"
      onWidgetsChange={onWidgetsChange}
      customActions={customActions}
    />
  );
};

export default AthleteDashboard;